import { useCallback, useEffect, useState } from "react";
import { ListTodo, Plus, Trash2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fmtD, fmtDT } from "@/lib/api";
import { Card } from "@/components/kit";
import { cn } from "@/lib/utils";

const EVT = "ka-actions-changed";
const changed = () => window.dispatchEvent(new Event(EVT));
const row = (title = "") => ({ title, assignee_id: "", due_date: "" });

function useActions(venueId) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/actions", { params: { venue_id: venueId } }).then((r) => setRows(r.data)).catch(() => setRows([])), [venueId]);
  useEffect(() => {
    load();
    window.addEventListener(EVT, load);
    return () => window.removeEventListener(EVT, load);
  }, [load]);
  return rows;
}

function useTeam() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data.filter((m) => m.active))).catch(() => {});
  }, []);
  return team;
}

async function tick(a, done) {
  try {
    await api.patch(`/actions/${a.id}`, { done });
    toast.success(done ? "Ticked off" : "Reopened");
    changed();
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function ActionRow({ a, admin }) {
  const remove = async () => {
    await api.delete(`/actions/${a.id}`);
    toast.success("Action removed");
    changed();
  };
  return (
    <li data-testid={`action-${a.id}`} className="flex items-start gap-3 py-2.5">
      <Checkbox data-testid={`action-tick-${a.id}`} checked={a.done} onCheckedChange={(v) => tick(a, !!v)} className="mt-0.5 h-5 w-5" />
      <span className="min-w-0 flex-1">
        <span className={cn("block text-sm font-semibold", a.done ? "text-slate-400 line-through" : "text-slate-900")}>{a.title}</span>
        <span className="block text-xs text-slate-500">
          {a.assignee_name || "Unassigned"}
          {a.due_date && <span className={cn(a.overdue && "font-bold text-rose-700")}> · {a.overdue ? "Overdue, was due" : "Due"} {fmtD(a.due_date)}</span>}
          {a.done && a.done_by && ` · fixed by ${a.done_by} ${fmtDT(a.done_at)}`}
          {a.source?.inspector && ` · from ${a.source.inspector}`}
        </span>
      </span>
      {admin && <button data-testid={`action-delete-${a.id}`} onClick={remove} className="ka-press rounded-full p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
    </li>
  );
}

export function CreateActionsDialog({ venueId, feedback, onClose }) {
  const team = useTeam();
  const [items, setItems] = useState(() => (feedback?.suggested?.length ? feedback.suggested.map(row) : [row()]));
  const [busy, setBusy] = useState(false);
  const set = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const save = async (e) => {
    e.preventDefault();
    const clean = items.filter((it) => it.title.trim()).map((it) => ({ title: it.title.trim(), assignee_id: it.assignee_id || null, due_date: it.due_date || null }));
    if (!clean.length) return toast.error("Add at least one action");
    setBusy(true);
    try {
      await api.post("/actions", { venue_id: venueId, feedback_id: feedback?.id || null, items: clean });
      toast.success(`${clean.length} action${clean.length === 1 ? "" : "s"} added`);
      changed();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{feedback ? "Turn feedback into actions" : "Add actions"}</DialogTitle></DialogHeader>
        {feedback?.comment && <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600"><b>{feedback.name}:</b> {feedback.comment}</p>}
        <form onSubmit={save} className="space-y-3">
          {items.map((it, i) => (
            <div key={i} data-testid={`action-form-row-${i}`} className="grid gap-2 rounded-xl border border-slate-200 p-3 sm:grid-cols-[1fr_160px_150px_auto]">
              <input data-testid={`action-title-input-${i}`} className="ka-input" placeholder="What needs fixing" maxLength={300} value={it.title} onChange={(e) => set(i, "title", e.target.value)} />
              <select data-testid={`action-assignee-select-${i}`} className="ka-input" value={it.assignee_id} onChange={(e) => set(i, "assignee_id", e.target.value)}><option value="">Who?</option>{team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
              <input data-testid={`action-due-input-${i}`} type="date" className="ka-input" value={it.due_date} onChange={(e) => set(i, "due_date", e.target.value)} />
              <button type="button" data-testid={`action-row-remove-${i}`} onClick={() => setItems(items.filter((_, j) => j !== i))} className="ka-press justify-self-end rounded-full p-2 text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="action-add-row-button" onClick={() => setItems([...items, row()])} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold hover:border-slate-900"><Plus className="h-4 w-4" />Another action</button>
            <button data-testid="action-save-button" disabled={busy} className="ka-press rounded-full bg-emerald-600 px-6 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy ? "Saving…" : "Save actions"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ActionPlanCard({ venueId }) {
  const rows = useActions(venueId);
  const [adding, setAdding] = useState(false);
  if (!rows) return null;
  const done = rows.filter((a) => a.done).length;
  const overdue = rows.filter((a) => a.overdue).length;
  const pct = rows.length ? Math.round((100 * done) / rows.length) : 0;
  return (
    <Card data-testid="action-plan-card" className="lg:col-span-2">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-display text-xl font-extrabold"><ListTodo className="h-5 w-5 text-slate-500" />Inspector action plan</div>
        {rows.length > 0 && <span data-testid="action-plan-progress" className="text-sm font-semibold text-slate-600">{done} of {rows.length} fixed</span>}
        {overdue > 0 && <span data-testid="action-plan-overdue" className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-bold text-rose-700">{overdue} overdue</span>}
        <button data-testid="action-plan-add-button" onClick={() => setAdding(true)} className="ka-press ml-auto inline-flex items-center gap-1 rounded-full border border-slate-300 px-4 py-1.5 text-sm font-semibold hover:border-slate-900"><Plus className="h-4 w-4" />Add action</button>
      </div>
      {rows.length > 0 && <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>}
      {rows.length === done && rows.length > 0 && <p data-testid="action-plan-all-done" className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" />Every point is fixed. Ready for the next visit.</p>}
      {!rows.length && <p className="mt-2 text-sm text-slate-500">No actions yet. Use "Turn into actions" on an inspector's feedback below, or add your own.</p>}
      <ul className="mt-2 divide-y divide-slate-100">{rows.map((a) => <ActionRow key={a.id} a={a} admin />)}</ul>
      {adding && <CreateActionsDialog venueId={venueId} onClose={() => setAdding(false)} />}
    </Card>
  );
}

export function MyActions({ venueId }) {
  const rows = useActions(venueId);
  const open = (rows || []).filter((a) => !a.done);
  if (!open.length) return null;
  return (
    <Card data-testid="my-actions-card">
      <div className="flex items-center gap-2 font-display text-lg font-extrabold"><ListTodo className="h-5 w-5 text-emerald-600" />Inspector actions to fix</div>
      <ul className="mt-2 divide-y divide-slate-100">{open.slice(0, 6).map((a) => <ActionRow key={a.id} a={a} admin={false} />)}</ul>
    </Card>
  );
}
